import cx from 'classnames';
import {memo, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import {EditButton} from '~/common/components/EditButton/EditButton';
import {eventDatesHaveSameDay} from '~/common/utils/time';
import {AddToCalendar} from '~/ViewInvitation/common/AddToCalendar/AddToCalendar';
import {Label} from '~/ViewInvitation/common/Label';
import {useConnectEditor} from '~/ViewInvitation/hooks/useConnectEditor';
import {LocationBlock} from '~/ViewInvitation/routes/Landing/LocationBlock';
import {
  selectEventEnd,
  selectEventStart,
  selectEventTimezone,
  selectLocationName,
  selectMeetingUrl,
  selectStreetAddress,
  selectTitle,
} from '~/ViewInvitation/selectors/eventDetails';
import type {ModuleProps} from '~/ViewInvitation/types/module';
import {isPreview} from '~/ViewInvitation/utils';
import {
  formatEventDateForView,
  formatEventTimeForView,
  formatEventTimespanForView,
  shouldShowYears,
} from '~/ViewInvitation/utils/date';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';

import './TitleModule.sass';

// TODO: let's rename this to EventDetails -- we need to update the pandemic keys as well
// TODO: since they already container an 'event_details', some cleanup required
export const TitleModule = memo(
  ({readonly = true, preview = isPreview(), editBtnRef}: ModuleProps) => {
    const title = useSelector(selectTitle);
    const start = useSelector(selectEventStart);
    const end = useSelector(selectEventEnd);
    const timezone = useSelector(selectEventTimezone);
    const locationName = useSelector(selectLocationName);
    const streetAddress = useSelector(selectStreetAddress);
    const meetingUrl = useSelector(selectMeetingUrl);
    const shouldShowLocationBlock = Boolean(locationName || streetAddress || meetingUrl);
    const startEndHaveSameDay = eventDatesHaveSameDay(start, end);
    const showStartDateOnly = (start && end && startEndHaveSameDay) || (start && !end);
    const showYear = shouldShowYears(start, end);
    const isLargishAndUp = useIsLargishAndUp();

    const titleContainerRef = useRef<HTMLDivElement>(null);
    const editTitleBtnRef = useRef<HTMLButtonElement>(null);
    const dateContainerRef = useRef<HTMLDivElement>(null);
    const editDateBtnRef = useRef<HTMLButtonElement>(null);

    const editable = preview && !readonly;
    const isMwAndEditable = editable && !isLargishAndUp;
    const isDesktopAndEditable = editable && isLargishAndUp;
    useConnectEditor(titleContainerRef, editTitleBtnRef, isDesktopAndEditable);
    useConnectEditor(dateContainerRef, editDateBtnRef, isDesktopAndEditable);

    const formattedTitle = useMemo(() => {
      if (!title?.trim()) return preview ? 'Your event title' : '';
      return title;
    }, [title, preview]);

    return (
      <>
        <EditButton data-qa-id="edit-title-module" ref={editBtnRef} hide={!isMwAndEditable} />
        <div className="title-module__container" id="event-title-module">
          <div
            className={cx(
              'view-invitation__details-module__section',
              'view-invitation__event-title',
              {
                editable: isDesktopAndEditable,
              }
            )}
            ref={titleContainerRef}
          >
            <EditButton
              data-qa-id="edit-event-title"
              ref={editTitleBtnRef}
              hide={!isDesktopAndEditable}
            />
            <Typography
              as="h2"
              variant={isLargishAndUp ? 'header3' : 'header4'}
              data-qa-id="event-title"
              className="view-invitation__event-title"
              color={title?.trim() || !preview ? 'charcoal' : 'pebble'}
            >
              {formattedTitle}
            </Typography>
          </div>
          <div
            className={cx('title-module__dates', 'view-invitation__details-module__section', {
              'title-module__dates--no-end': showStartDateOnly,
              editable: isDesktopAndEditable,
            })}
            ref={dateContainerRef}
          >
            <EditButton
              data-qa-id="edit-event-date"
              ref={editDateBtnRef}
              hide={!isDesktopAndEditable}
            />
            {!start ? (
              <div className="title-module__date">
                <Label value="Date" data-qa-id="start" />
                <Typography variant="paragraph2">–</Typography>
              </div>
              // biome-ignore lint/style/noNestedTernary: this is simple enough
            ) : showStartDateOnly ? (
              <>
                <div className="title-module__date">
                  <Label value="Date" data-qa-id="start" />
                  <Typography variant="paragraph2">
                    {formatEventDateForView(start, timezone, showYear)}
                  </Typography>
                  <Typography variant="paragraph2">
                    {end
                      ? formatEventTimespanForView(start, end, timezone)
                      : formatEventTimeForView(start, timezone)}
                  </Typography>
                </div>
                <div className="title-module__add-to-calendar">
                  <AddToCalendar />
                </div>
              </>
            ) : (
              <>
                <div className="title-module__start-date">
                  <Label value="Start Date" data-qa-id="start" />
                  <Typography variant="paragraph2">
                    {formatEventDateForView(start, timezone, showYear)}
                  </Typography>
                  <Typography variant="paragraph2">
                    {formatEventTimeForView(start, timezone)}
                  </Typography>
                </div>
                <div className="title-module__end-date">
                  <Label value="End Date" data-qa-id="end" />
                  <Typography variant="paragraph2">
                    {formatEventDateForView(end, timezone, showYear)}
                  </Typography>
                  <Typography variant="paragraph2">
                    {formatEventTimeForView(end, timezone)}
                  </Typography>
                </div>
                <div className="title-module__add-to-calendar">
                  <AddToCalendar />
                </div>
              </>
            )}
          </div>
          {shouldShowLocationBlock && (
            <LocationBlock className="title-module__location" editable={isDesktopAndEditable} />
          )}
        </div>
      </>
    );
  }
);

TitleModule.displayName = 'TitleModule';
